import { LegalPageLayout, P, UL, LI, Strong, ContactLink } from './LegalPageLayout';

/**
 * Acceptable Use Policy for PlyEZ (plyez.com). Supplements the Terms of
 * Service and sets the boundaries on how the Service — a finance back-office
 * product handling payments, ledgers, and sensitive vendor data — may be
 * used. Effective 2026-06-03. We recommend reviewing it with your own
 * counsel; it reflects our current practices.
 */
export function AcceptableUsePage() {
  return (
    <LegalPageLayout
      i18nKey="legal.aup"
      lastUpdated="2026-06-03"
      lead={
        <>
          This Acceptable Use Policy (the &ldquo;Policy&rdquo;) describes activities
          that are prohibited on <Strong>PlyEZ</Strong>. It supplements and is
          incorporated into the{' '}
          <ContactLink to="/legal/terms">Terms of Service</ContactLink>; capitalized
          terms have the meanings given there. Because PlyEZ moves payment
          instructions and stores sensitive financial records, these rules protect
          every customer on the platform. Violating this Policy is a breach of the
          Terms and may result in suspension or termination.
        </>
      }
      sections={[
        {
          id: 'illegal',
          title: '1. Illegal and harmful use',
          body: (
            <>
              <P>You may not use the Service to engage in, facilitate, or conceal:</P>
              <UL>
                <LI>fraud, deception, money laundering, terrorism financing, or any financial crime;</LI>
                <LI>tax evasion, falsification of financial or accounting records, or the creation of misleading audit trails;</LI>
                <LI>processing payments for prohibited or illegal goods and services, including narcotics, illegal weapons, human trafficking, and child sexual abuse material;</LI>
                <LI>activity that infringes the intellectual-property, privacy, publicity, or other rights of any third party;</LI>
                <LI>any other activity that violates applicable law or regulation in any jurisdiction in which you operate or in which the Service is provided.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'fraud-aml',
          title: '2. Fraud, sanctions, and AML/CFT',
          body: (
            <>
              <P>
                The Service orchestrates payments through regulated partners (including
                Stripe and Wise). You must use it consistent with applicable
                anti-money-laundering, counter-terrorist-financing, and sanctions laws.
                You may not:
              </P>
              <UL>
                <LI>transact with, or route funds to or from, parties subject to sanctions administered by Japan, the United States (OFAC), the European Union, the United Kingdom, or the United Nations, or located in comprehensively sanctioned regions;</LI>
                <LI>structure, mislabel, or split transactions to evade reporting thresholds, screening, or controls;</LI>
                <LI>impersonate a vendor or payee, alter banking details to misdirect funds, or facilitate business-email-compromise or invoice-redirection schemes;</LI>
                <LI>submit payment instructions you are not authorized to make, or use the Service to move funds you do not have a lawful right to move.</LI>
              </UL>
              <P>
                You are responsible for your own customer due diligence and for
                verifying payee identity and banking details before approving payments.
              </P>
            </>
          ),
        },
        {
          id: 'platform',
          title: '3. Platform integrity and reverse engineering',
          body: (
            <>
              <P>You may not, and may not permit any Authorized User or third party to:</P>
              <UL>
                <LI>reverse-engineer, decompile, disassemble, or attempt to derive the source code, models, or underlying ideas of the Service, except to the extent this restriction is prohibited by applicable law;</LI>
                <LI>copy, frame, mirror, resell, sublicense, or make the Service available to anyone not authorized under your subscription, or use it to build a competing product;</LI>
                <LI>remove or obscure proprietary notices, or circumvent license, seat, or entitlement controls;</LI>
                <LI>scrape, crawl, or harvest data or pages from the Service except through documented API endpoints used within their published limits;</LI>
                <LI>introduce malware, worms, trojans, or other harmful code, or attempt prompt-injection or similar techniques to manipulate AI Features into unsafe or unauthorized behavior.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'data',
          title: '4. Data and content',
          body: (
            <>
              <P>You may not:</P>
              <UL>
                <LI>upload or process content you do not have the lawful right to use;</LI>
                <LI>upload personal data of third parties without a valid lawful basis, or upload special-category personal data (such as health, biometric, racial, or religious data) beyond what the back-office finance use case requires;</LI>
                <LI>attempt to extract training data from, or reconstruct the parameters of, the underlying AI models, or use outputs to do so;</LI>
                <LI>use the Service to send unsolicited bulk or spam communications, or to store content unrelated to your legitimate back-office operations.</LI>
              </UL>
            </>
          ),
        },
        {
          id: 'limits',
          title: '5. Rate, automation, and fair-use limits',
          body: (
            <>
              <P>
                Plans include a reasonable volume of usage. To keep the Service
                reliable for everyone, you must respect published API rate limits, AI
                spend caps, and technical quotas, and you may not:
              </P>
              <UL>
                <LI>bypass, disable, or artificially inflate usage against rate limits, AI caps, or other access controls;</LI>
                <LI>run automation, bots, or scripts that materially exceed documented limits or that generate load disproportionate to normal back-office use (for example mass-uploading unrelated content or hammering AI endpoints);</LI>
                <LI>use a single account or seat to serve multiple distinct organizations, or otherwise share seats among individuals.</LI>
              </UL>
              <P>
                We may investigate, throttle, or require an upgrade for accounts that
                demonstrably abuse fair use, with notice where practicable.
              </P>
            </>
          ),
        },
        {
          id: 'security',
          title: '6. Security testing and responsible disclosure',
          body: (
            <>
              <P>
                We welcome good-faith security research, but only within these
                boundaries. You may not probe, scan, or test the vulnerability of the
                Service, or attempt to breach authentication, authorization, or
                tenant-isolation controls, except as follows:
              </P>
              <UL>
                <LI>testing is limited to accounts and data you own or are expressly authorized to access — never another customer&rsquo;s workspace or data;</LI>
                <LI>you must not perform denial-of-service or load testing, use automated mass exploitation, or degrade the Service for others;</LI>
                <LI>you must not access, modify, exfiltrate, or retain data that is not yours, and must stop and report as soon as a vulnerability is confirmed;</LI>
                <LI>report findings promptly and confidentially to <a className="text-accent-700 underline" href="mailto:security@plyez.com">security@plyez.com</a> and allow reasonable time to remediate before any disclosure.</LI>
              </UL>
              <P>
                We will not pursue good-faith research that follows these rules and acknowledge credible reports within one business day.
              </P>
            </>
          ),
        },
        {
          id: 'enforcement',
          title: '7. Consequences and enforcement',
          body: (
            <>
              <P>
                We may investigate suspected violations of this Policy and cooperate
                with law-enforcement and regulatory authorities where required. In
                response to a violation, or where necessary to protect the Service, its
                users, or third parties, we may — with notice where practicable, and
                immediately where the risk is serious — remove or disable offending
                content, throttle usage, suspend an Authorized User or the account, or
                terminate the subscription under the Terms of Service.
              </P>
              <P>
                Where a violation involves payments, we and our payment partners may
                hold, screen, reverse, or decline affected transactions. Termination for
                a Policy violation does not entitle you to a refund and does not limit
                any other remedy available to us.
              </P>
            </>
          ),
        },
        {
          id: 'reporting',
          title: '8. Reporting and contact',
          body: (
            <>
              <P>
                Report suspected abuse, fraud, or content violations to{' '}
                <a className="text-accent-700 underline" href="mailto:support@plyez.com">support@plyez.com</a>,
                security vulnerabilities to{' '}
                <a className="text-accent-700 underline" href="mailto:security@plyez.com">security@plyez.com</a>,
                and legal or policy questions to{' '}
                <a className="text-accent-700 underline" href="mailto:legal@plyez.com">legal@plyez.com</a>.
                We may update this Policy from time to time; material changes are
                announced as described in the{' '}
                <ContactLink to="/legal/terms">Terms of Service</ContactLink>.
              </P>
            </>
          ),
        },
      ]}
    />
  );
}
